import React, { useRef, useCallback } from 'react';
import { Sparkles } from 'lucide-react';

// Shared mm:ss formatter — reused by EditorClipList, EditorMultiTrackTimeline,
// EditorPreviewPlayer and EditorEditingToolbar so every clock in the Editor
// reads identically.
export const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec.toString().padStart(2, '0')}`;
};

/**
 * Source-level timeline ruler: shows every clip of the active output type as
 * a block, a draggable playhead, and accepted AI-suggestion markers. Click
 * anywhere to seek; click a block to select that clip.
 */
export function EditorTimelineRuler({ duration, currentTime, clips, selectedClipId, aiMarkers, onSeek, onSelectClip }) {
  const trackRef = useRef(null);
  const safeDuration = duration > 0 ? duration : 1;

  const timeFromEvent = useCallback((e) => {
    const rect = trackRef.current.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    return pct * safeDuration;
  }, [safeDuration]);

  const handleTrackClick = (e) => {
    onSeek(timeFromEvent(e));
  };

  return (
    <div className="space-y-1" data-testid="editor-timeline">
      <div
        ref={trackRef}
        onClick={handleTrackClick}
        className="relative h-16 rounded-lg bg-black/40 border border-white/10 cursor-pointer overflow-hidden"
        data-testid="editor-timeline-track"
      >
        {aiMarkers?.filter((m) => m.status === 'accepted').map((m) => (
          <div
            key={m.id}
            className="absolute top-0 h-full bg-amber-400/15 border-x border-amber-400/40"
            style={{ left: `${(m.start / safeDuration) * 100}%`, width: `${((m.end - m.start) / safeDuration) * 100}%` }}
            title={m.label}
            data-testid={`timeline-ai-marker-${m.id}`}
          >
            <Sparkles className="h-3 w-3 text-amber-400 absolute top-1 left-1" />
          </div>
        ))}

        {clips.map((clip) => (
          <div
            key={clip.id}
            onClick={(e) => { e.stopPropagation(); onSelectClip(clip.id); onSeek(clip.in_point); }}
            className={`absolute top-2 bottom-2 rounded-md border-2 flex items-center px-2 transition-all ${
              clip.id === selectedClipId
                ? 'bg-[#3A7BFF]/30 border-[#3A7BFF]'
                : clip.approved
                ? 'bg-green-500/15 border-green-500/40 hover:border-green-400'
                : 'bg-white/5 border-white/20 hover:border-white/40'
            }`}
            style={{
              left: `${(clip.in_point / safeDuration) * 100}%`,
              width: `${Math.max(0.5, ((clip.out_point - clip.in_point) / safeDuration) * 100)}%`,
            }}
            data-testid={`timeline-clip-${clip.id}`}
          >
            <span className="text-[11px] text-white truncate font-medium">{clip.name}</span>
          </div>
        ))}

        <div
          className="absolute top-0 bottom-0 w-0.5 bg-pink-500 z-10 pointer-events-none"
          style={{ left: `${(Math.min(currentTime, safeDuration) / safeDuration) * 100}%` }}
          data-testid="editor-timeline-playhead"
        />
      </div>
      <div className="flex justify-between text-[11px] text-[#8A93A6]">
        <span>0:00</span>
        <span data-testid="editor-current-time">{fmtTime(currentTime)} / {fmtTime(safeDuration)}</span>
      </div>
    </div>
  );
}
